import { CHECK_IN_MOOD_GROUP_KEY, type CheckInData } from './schema';

// Canonical stored check-in payload fixtures — the one place the "shape a
// check-in write leaves in `activity_events.payload`" is written down for
// tests. The write path and every prompt-context reader consume THESE (never
// hand-rolled payload literals), so a change to the stored shape breaks one
// fixture visibly instead of letting a reader drift silently. Both fixtures
// must always parse against `checkInDataSchema` (see fixtures.test.ts).

/**
 * The current write shape: mood arrives as tags in the server-served
 * `mood` group, other groups carry catalog + member-authored custom tags (the
 * second `who` tag is member-authored), the note is real member prose, and
 * entries carry the per-program quantities. The legacy `moodBefore`/`moodAfter`
 * fields are never written by this shape.
 *
 * The group keys track whatever the catalog currently serves, so that a reader
 * which quietly handles only a retired vocabulary fails here first. Payloads
 * written under a retired grouping are still valid and are covered by
 * `legacyShapeCheckInPayload` — readers must keep rendering both.
 */
export function newShapeCheckInPayload(overrides: Partial<CheckInData> = {}): CheckInData {
  return {
    date: '2026-07-07',
    context: {
      [CHECK_IN_MOOD_GROUP_KEY]: ['Stressed', 'Tired'],
      where: ['At home'],
      who: ['Friends', 'Late shift with the team'],
    },
    notes: 'Long day but I stuck to the plan I made with Sona.',
    moodBefore: null,
    moodAfter: null,
    entries: [
      {
        memberProgramId: '22222222-2222-4222-8222-222222222222',
        programKey: 'alcohol',
        quantity: 2,
        isFree: false,
        // The write path stores no unit today (presentation is deferred to the
        // renderable-schema work); the fixture mirrors the REAL stored shape.
        // Readers that render units must carry their own unit-bearing case.
        unit: null,
      },
      {
        memberProgramId: '33333333-3333-4333-8333-333333333333',
        programKey: 'nicotine',
        quantity: 0,
        isFree: true,
        unit: null,
      },
    ],
    ...overrides,
  };
}

/**
 * The legacy stored shape (older writes and the dev seed): mood lives in
 * `moodAfter`/`moodBefore`, context groups predate the served catalog (the dev
 * seeder's `social`, and the retired combined `context` group), and entries may
 * be empty. Readers must keep rendering this shape unchanged — stored history is
 * never migrated when the served vocabulary changes.
 */
export function legacyShapeCheckInPayload(overrides: Partial<CheckInData> = {}): CheckInData {
  return {
    date: '2026-07-07',
    context: { social: ['dinner'], context: ['Work stress'] },
    notes: 'Skipped drinks at the work dinner and felt good about it.',
    moodBefore: null,
    moodAfter: 'steadier',
    entries: [],
    ...overrides,
  };
}
